import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah85iboh653ivfvnns0";

type Status = "idle" | "loading" | "success" | "error";

export default function NewsletterForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);
    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    payload.delete("website_alt");

    setStatus("loading");
    setFormError("");
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      id="newsletter-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="w-full"
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center">
            <i className="ri-mail-line text-base text-secondary-500" aria-hidden="true" />
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="输入你的邮箱，第一时间收到独家推送"
            className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <input
          className="form-honeypot"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" aria-hidden="true" />
              免费订阅
            </>
          )}
        </button>
      </div>

      {status === "success" && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-accent-700">
          <i className="ri-checkbox-circle-line" aria-hidden="true" />
          订阅成功，我们会在下一次独家发布时通知你。
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-700">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {formError}
        </p>
      )}
      {status === "idle" && (
        <p className="mt-3 text-xs text-foreground-600">
          每周两到三次精选推送，可随时退订。
        </p>
      )}
    </form>
  );
}